import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LibraryModel, Machine } from '@couch/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api, errorMessage } from '../api/client';
import { LIBRARY_KEY, libraryApi } from '../api/storage';
import { formatBytes } from './machine/format';
import { LibraryUploads } from './library/LibraryUploads';
import { MACHINES_KEY } from './machines/MachineActions';

/** One model in the library: install it on an online machine, or delete it from the bucket. */
function LibraryRow({ model, machines }: { model: LibraryModel; machines: Machine[] }) {
  const queryClient = useQueryClient();
  const [override, setOverride] = useState('');
  const target = override || machines[0]?.id || '';
  const [note, setNote] = useState('');
  const install = useMutation({
    mutationFn: () => libraryApi.install(target, model.folder, model.fileName),
    onSuccess: () => { setNote(`Installing on ${machines.find((m) => m.id === target)?.name ?? 'the machine'} — see its Downloads.`); },
  });
  const remove = useMutation({
    mutationFn: () => libraryApi.remove(model.folder, model.fileName),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIBRARY_KEY }),
  });
  const ask = () => {
    if (window.confirm(`Delete ${model.fileName} (${formatBytes(model.sizeBytes)}) from your library? Machines keep their own copies.`)) remove.mutate();
  };
  return (
    <li className="model-row">
      <span className="model-name">{model.fileName}</span> <span className="muted">{model.folder} · {formatBytes(model.sizeBytes)}</span>
      <span className="model-actions">
        {machines.length > 0 && (
          <select aria-label={`Machine for ${model.fileName}`} value={target} onChange={(e) => { setOverride(e.target.value); }}>
            {machines.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        )}
        <button type="button" className="button-quiet" disabled={!target || install.isPending} onClick={() => { setNote(''); install.mutate(); }}>Install</button>
        <button type="button" className="button-quiet" disabled={remove.isPending} aria-label={`Delete ${model.fileName} from the library`} onClick={ask}>Delete</button>
      </span>
      {note && <p role="status">{note}</p>}
      {(install.isError || remove.isError) && <p role="alert" className="form-error">{errorMessage(install.error ?? remove.error)}</p>}
    </li>
  );
}

/** The model library in the user's bucket (doc 75). */
export function LibraryPage() {
  const library = useQuery({ queryKey: LIBRARY_KEY, queryFn: libraryApi.list, retry: false });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const online = (machines.data?.machines ?? []).filter((m) => m.online);
  const notConnected = library.error instanceof ApiError && library.error.status === 409;
  return (
    <div className="library">
      <h1>Library</h1>
      <p className="muted">
        Models kept once in your own storage. Send one from a machine (“To library” on its model list) and install it on any other —
        a new cloud machine fetches it from your bucket instead of the internet.
      </p>
      <section className="panel" aria-labelledby="library-title">
        <h2 id="library-title">Models in your library</h2>
        <LibraryUploads />
        {library.isPending && <p role="status">Loading…</p>}
        {notConnected && <p>{errorMessage(library.error)}. <Link to="/connections">Open Connections</Link></p>}
        {library.isError && !notConnected && <p role="alert" className="form-error">{errorMessage(library.error)}</p>}
        {library.data?.models.length === 0 && <p className="muted">Nothing in the library yet.</p>}
        {library.data && library.data.models.length > 0 && online.length === 0 && <p className="muted">Switch on a machine to install from the library.</p>}
        <ul>{library.data?.models.map((m) => <LibraryRow key={`${m.folder}/${m.fileName}`} model={m} machines={online} />)}</ul>
        {library.data?.truncated && <p className="muted">Only the first 20 000 files are shown.</p>}
        {library.data && <p className="muted">Signed by {library.data.via === 'couch' ? 'your key on Couch' : library.data.via}.</p>}
      </section>
    </div>
  );
}
